import React, { useEffect, useRef, useState } from 'react'

import { ErrorMessage, Field, FieldHookConfig, useField } from 'formik'

export type InputFieldProps = {
  label?: string
  parentClassName?: string
  placeholder?: string
  style?: 'minimal' | 'default'
  showError?: boolean
  customClasses?: string
} & FieldHookConfig<string>

/**
 * @deprecated Use the Formik Field and customize at the feature level
 * @see useForm() hook
 */
const InputField = (props: InputFieldProps) => {
  const {
    label,
    parentClassName = '',
    placeholder,
    style = 'minimal',
    showError = true,
    customClasses = '',
    ...fieldHookConfig
  } = props

  const [field, meta, helpers] = useField(fieldHookConfig)
  const isDateField = props.type === 'date'
  const [showPlaceholder, setShowPlaceholder] = useState(!field.value)
  const inputRef = useRef(null)

  const fieldClasses = style === 'minimal' ? 'minimal-input' : 'classic-input'
  const actualPlaceholder = placeholder ? placeholder : '~'

  const handleFocus = () => {
    setShowPlaceholder(false)
    if (isDateField && placeholder) inputRef.current.showPicker()
  }

  const handleBlur = (e) => {
    setShowPlaceholder(!e.target.value)
    field.onBlur(e) // Call Formik's onBlur to set touched
    if (props?.onBlur) props.onBlur(e) // Call any additional onBlur handler from props
  }

  useEffect(() => {
    setShowPlaceholder(!field.value)
  }, [field.value])

  return (
    <div className={`${parentClassName} relative`}>
      {!!label && (
        <label
          htmlFor={fieldHookConfig.name}
          className='text-sm text-gray-700 block mb-2'
        >
          {label}
        </label>
      )}
      {isDateField && showPlaceholder && placeholder && (
        <div
          className={`${fieldClasses} ${customClasses} absolute top-1/2 left-0 transform -translate-y-1/2 pointer-events-none text-gray-500 pl-3 bg-white h-full text-base content-center`}
        >
          {actualPlaceholder}
        </div>
      )}
      <Field
        {...field}
        {...fieldHookConfig}
        type={props.type}
        innerRef={inputRef}
        className={`${fieldClasses} ${customClasses}`}
        placeholder={isDateField ? ' ' : actualPlaceholder}
        onFocus={handleFocus}
        onBlur={handleBlur} // Properly call handleBlur to ensure touched is set
      />
      {showError && meta.touched && meta.error && (
        <div className='text-red-500'>
          <ErrorMessage
            name={fieldHookConfig.name}
            component='span'
          />
        </div>
      )}
    </div>
  )
}

export default InputField
